import React, { useState, useEffect, useRef } from 'react';
import DocModal from '../components/DocModal';
import {
  Users, Store, ShoppingBag, TrendingUp, CheckCircle, XCircle,
  Shield, Package, AlertCircle, Phone, MapPin, Search, Eye, X,
  Navigation, ChevronDown, ChevronUp, Settings, Gift, Save, Truck, Image, Palette,
  MessageSquare, Send, CheckCircle2, Clock
} from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import Navbar from '../components/Navbar';
import API from '../utils/api';
import { toast } from 'react-toastify';
import { useBranding } from '../context/BrandingContext';

const TABS = ['overview', 'owners', 'customers', 'orders', 'referrals', 'settings', 'user_chats', 'owner_chats'];

const HOME_CATEGORY_OPTIONS = [
  { key: 'sand', label: 'Sand', desc: 'River, M-sand' },
  { key: 'cement', label: 'Cement', desc: 'OPC, PPC' },
  { key: 'iron', label: 'Iron/Steel', desc: 'TMT bars, rods' },
  { key: 'bricks', label: 'Bricks', desc: 'Standard, fly ash' },
  { key: 'long_bricks', label: 'Long Bricks', desc: 'Hollow, solid' },
  { key: 'short_bricks', label: 'Short Bricks', desc: 'Paving, interlocking' },
  { key: 'other', label: 'Hardware', desc: 'Tools, plumbing, electrical' },
];

const THEME_OPTIONS = [
  { key: 'graphite', name: 'Graphite Pro', colors: ['#334155', '#F59E0B'], note: 'Premium hardware-store style' },
  { key: 'forest', name: 'Forest Supply', colors: ['#15803D', '#84CC16'], note: 'Fresh, trusted marketplace' },
  { key: 'steel', name: 'Steel Blue', colors: ['#2563EB', '#06B6D4'], note: 'Clean industrial theme' },
  { key: 'brick', name: 'Brick Earth', colors: ['#B45309', '#EA580C'], note: 'Warm construction material tone' },
  { key: 'indigo', name: 'Indigo Trade', colors: ['#4F46E5', '#8B5CF6'], note: 'Modern B2B marketplace' },
  { key: 'teal', name: 'Teal Works', colors: ['#0F766E', '#2DD4BF'], note: 'Calm professional supply theme' }
];

const slugCategoryKey = (value) => String(value || '')
  .trim()
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '_')
  .replace(/^_+|_+$/g, '')
  .slice(0, 40);

const withDefaultCategories = (settings = {}) => ({
  ...settings,
  homeCategories: (settings.homeCategories || []).length
    ? settings.homeCategories
    : HOME_CATEGORY_OPTIONS.map(cat => ({ ...cat, image: '', active: true }))
});

const STATUS_COLORS = {
  pending: '#F59E0B', approved: 'var(--green)', rejected: 'var(--red)',
  confirmed: '#3B82F6', processing: '#8B5CF6',
  out_for_delivery: 'var(--orange)', delivered: 'var(--green)', cancelled: 'var(--red)'
};

// ── Owner Card ────────────────────────────────────────────────────────────
function OwnerCard({ owner, onVerify, onViewDocs }) {
  const [expanded, setExpanded] = useState(false);
  const vs = owner.verificationStatus;

  return (
    <div style={{ background: 'white', borderRadius: 14, padding: 16, boxShadow: 'var(--shadow)' }}>
      {/* Top row */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <div style={{ width: 44, height: 44, background: 'linear-gradient(135deg,var(--orange),var(--amber))', borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 800, fontSize: 17, flexShrink: 0 }}>
          {owner.shopName?.[0]}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap', marginBottom: 3 }}>
            <span style={{ fontWeight: 700, fontSize: 15 }}>{owner.shopName}</span>
            <span style={{ padding: '2px 9px', borderRadius: 20, fontSize: 11, fontWeight: 700,
              background: vs === 'approved' ? '#ECFDF5' : vs === 'rejected' ? '#FEF2F2' : '#FFFBEB',
              color: STATUS_COLORS[vs] || 'var(--gray)' }}>
              {vs?.toUpperCase()}
            </span>
          </div>
          <div style={{ fontSize: 12, color: 'var(--gray)', display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}><Phone size={10} /> {owner.user?.phone}</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}><MapPin size={10} /> {owner.location?.city}, {owner.location?.state}</span>
          </div>
          <div style={{ fontSize: 11, color: 'var(--gray)', marginTop: 2 }}>{owner.user?.email}</div>
        </div>
      </div>

      {/* Action buttons row — below info, full width, no overflow */}
      <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
        {/* View Docs */}
        <button onClick={() => onViewDocs(owner)}
          style={{ display: 'flex', alignItems: 'center', gap: 5, background: '#EFF6FF', border: '1.5px solid #BFDBFE', borderRadius: 8, padding: '7px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer', color: '#1D4ED8', flex: 1, justifyContent: 'center', minWidth: 80 }}>
          <Eye size={13} /> View Docs
        </button>

        {/* Approve */}
        {vs !== 'approved' && (
          <button onClick={() => onVerify(owner._id, 'approved')}
            style={{ display: 'flex', alignItems: 'center', gap: 5, background: 'var(--green)', color: 'white', border: 'none', borderRadius: 8, padding: '7px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer', flex: 1, justifyContent: 'center', minWidth: 80 }}>
            <CheckCircle size={13} /> Approve
          </button>
        )}

        {/* Revoke / Reject */}
        {vs !== 'rejected' && (
          <button onClick={() => onVerify(owner._id, 'rejected')}
            style={{ display: 'flex', alignItems: 'center', gap: 5, background: '#FEF2F2', color: 'var(--red)', border: '1.5px solid #FECACA', borderRadius: 8, padding: '7px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer', flex: 1, justifyContent: 'center', minWidth: 80 }}>
            <XCircle size={13} /> {vs === 'approved' ? 'Revoke' : 'Reject'}
          </button>
        )}

        {/* Re-approve after rejected */}
        {vs === 'rejected' && (
          <button onClick={() => onVerify(owner._id, 'approved')}
            style={{ display: 'flex', alignItems: 'center', gap: 5, background: '#ECFDF5', color: 'var(--green)', border: '1.5px solid #A7F3D0', borderRadius: 8, padding: '7px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer', flex: 1, justifyContent: 'center', minWidth: 80 }}>
            <CheckCircle size={13} /> Re-approve
          </button>
        )}
      </div>

      {/* Expand toggle */}
      <button onClick={() => setExpanded(!expanded)}
        style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', color: 'var(--gray)', cursor: 'pointer', fontSize: 11, marginTop: 10, padding: '2px 0' }}>
        {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
        {expanded ? 'Hide details' : 'More details'}
      </button>

      {expanded && (
        <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--gray-border)', display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: 8 }}>
          {[
            ['Address', owner.location?.address || '—'],
            ['Pincode', owner.location?.pincode || '—'],
            ['Rating', `${owner.rating?.toFixed(1) || '0'} ★ (${owner.totalRatings || 0})`],
            ['Shop Status', owner.isOpen ? 'Open' : 'Closed'],
            ['GPS', owner.location?.lat ? `${Number(owner.location.lat).toFixed(4)}, ${Number(owner.location.lng).toFixed(4)}` : 'No GPS'],
            ['Documents', owner.documents?.aadharFileName ? `Aadhar: ${owner.documents.aadharFileName}` : 'No docs uploaded'],
          ].map(([k, v]) => (
            <div key={k} style={{ background: 'var(--gray-light)', borderRadius: 8, padding: '8px 10px' }}>
              <div style={{ fontSize: 9, fontWeight: 700, color: 'var(--gray)', textTransform: 'uppercase', marginBottom: 2 }}>{k}</div>
              <div style={{ fontSize: 12, fontWeight: 600 }}>{v}</div>
            </div>
          ))}
          {owner.description && (
            <div style={{ background: 'var(--gray-light)', borderRadius: 8, padding: '8px 10px', gridColumn: '1 / -1' }}>
              <div style={{ fontSize: 9, fontWeight: 700, color: 'var(--gray)', textTransform: 'uppercase', marginBottom: 2 }}>Description</div>
              <div style={{ fontSize: 12, lineHeight: 1.5 }}>{owner.description}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main Admin Dashboard ──────────────────────────────────────────────────
export default function AdminDashboard() {
  const [searchParams] = useSearchParams();
  const urlTab = searchParams.get('tab');
  const urlTicket = searchParams.get('ticket');

  const [tab, setTab] = useState(TABS.includes(urlTab) ? urlTab : 'overview');
  const [ownerFilter, setOwnerFilter] = useState('all');
  const [stats, setStats] = useState({});
  const [owners, setOwners] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [referralPayouts, setReferralPayouts] = useState([]);
  const [openUserChatsCount, setOpenUserChatsCount] = useState(0);
  const [openOwnerChatsCount, setOpenOwnerChatsCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [docOwner, setDocOwner] = useState(null);
  const [settings, setSettings] = useState({
    referral: { enabled: true, customerReward: 20, ownerReward: 50 },
    payment: { advanceAmount: 499 }
  });
  const [settingsSaving, setSettingsSaving] = useState(false);
  const { refreshBranding } = useBranding();

  useEffect(() => {
    if (urlTab && TABS.includes(urlTab)) {
      setTab(urlTab);
    }
  }, [urlTab]);

  useEffect(() => { fetchAll(); }, []);

  const fetchChatCounts = async () => {
    try {
      const res = await API.get('/support/admin/unread-counts');
      setOpenUserChatsCount(res.data.userChatsUnread || 0);
      setOpenOwnerChatsCount(res.data.ownerChatsUnread || 0);
    } catch {}
  };

  useEffect(() => {
    fetchChatCounts();
    const interval = setInterval(fetchChatCounts, 15000);
    return () => clearInterval(interval);
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [dashRes, ownersRes, usersRes, ordersRes, settingsRes, payoutsRes] = await Promise.all([
        API.get('/admin/dashboard').catch(() => ({ data: {} })),
        API.get('/admin/owners').catch(() => ({ data: [] })),
        API.get('/admin/users').catch(() => ({ data: [] })),
        API.get('/admin/orders').catch(() => ({ data: [] })),
        API.get('/admin/settings').catch(() => ({ data: {} })),
        API.get('/admin/referral-payouts').catch(() => ({ data: [] })),
      ]);
      setStats(dashRes.data || {});
      setOwners(ownersRes.data || []);
      setCustomers((usersRes.data || []).filter(u => u.role === 'customer'));
      setOrders(ordersRes.data || []);
      const savedTheme = localStorage.getItem('ms_theme');
      setSettings(withDefaultCategories({
        ...settingsRes.data,
        branding: {
          ...(settingsRes.data.branding || {}),
          theme: THEME_OPTIONS.some(theme => theme.key === (settingsRes.data.branding?.theme || savedTheme))
            ? (settingsRes.data.branding?.theme || savedTheme)
            : 'forest'
        }
      }));
      setReferralPayouts(payoutsRes.data);
    } catch { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  };

  const verifyOwner = async (ownerId, status) => {
    try {
      await API.put(`/admin/owners/${ownerId}/verify`, { status });
      setOwners(prev => prev.map(o => o._id === ownerId ? { ...o, verificationStatus: status } : o));
      toast.success(`Owner ${status}`);
    } catch { toast.error('Failed'); }
  };

  const toggleUser = async (userId) => {
    try {
      const res = await API.put(`/admin/users/${userId}/toggle`);
      setCustomers(prev => prev.map(u => u._id === userId ? { ...u, isActive: res.data.isActive } : u));
      toast.success('Updated');
    } catch { toast.error('Failed'); }
  };

  const updateOrder = async (orderId, statusOrUpdates) => {
    try {
      const updates = typeof statusOrUpdates === 'string' ? { status: statusOrUpdates } : statusOrUpdates;
      await API.put(`/admin/orders/${orderId}`, updates);
      setOrders(prev => prev.map(o => o._id === orderId ? { ...o, ...updates } : o));
      toast.success('Order updated');
    } catch { toast.error('Failed'); }
  };

  const saveSettings = async () => {
    setSettingsSaving(true);
    try {
      const res = await API.put('/admin/settings', settings);
      setSettings(withDefaultCategories(res.data));
      await refreshBranding();
      toast.success('Settings saved');
    } catch { toast.error('Failed to save settings'); }
    finally { setSettingsSaving(false); }
  };

  const selectTheme = async (themeKey) => {
    localStorage.setItem('ms_theme', themeKey);
    const nextSettings = {
      ...settings,
      branding: { ...(settings.branding || {}), theme: themeKey }
    };
    setSettings(nextSettings);
    setSettingsSaving(true);
    try {
      const res = await API.put('/admin/settings', { branding: { theme: themeKey } });
      setSettings(withDefaultCategories({
        ...res.data,
        branding: { ...(res.data.branding || {}), theme: themeKey }
      }));
      await refreshBranding();
      toast.success('Theme applied');
    } catch {
      toast.error('Failed to apply theme');
    } finally {
      setSettingsSaving(false);
    }
  };

  const markReferralPaid = async (userId) => {
    if (!window.confirm('Mark all pending referral rewards paid for this user?')) return;
    try {
      const res = await API.put(`/admin/referral-payouts/${userId}/pay`);
      toast.success(`Paid ₹${Number(res.data.paidNow || 0).toLocaleString()}`);
      const payoutsRes = await API.get('/admin/referral-payouts');
      setReferralPayouts(payoutsRes.data);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to mark paid');
    }
  };

  const uploadBrandImage = (field, file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image must be 2MB or smaller');
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      setSettings(s => ({
        ...s,
        branding: {
          ...(s.branding || {}),
          [field]: e.target.result
        }
      }));
    };
    reader.readAsDataURL(file);
  };

  const uploadHomeBrandImage = (index, file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image must be 2MB or smaller');
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      setSettings(s => ({
        ...s,
        homeBrands: (s.homeBrands || []).map((brand, i) => i === index ? { ...brand, image: e.target.result } : brand)
      }));
    };
    reader.readAsDataURL(file);
  };

  const uploadHomeCategoryImage = (key, file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image must be 2MB or smaller');
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      setSettings(s => {
        const existing = s.homeCategories || [];
        const option = HOME_CATEGORY_OPTIONS.find(c => c.key === key) || { key, label: key, desc: '' };
        const nextItem = existing.find(c => c.key === key)
          ? existing.map(c => c.key === key ? { ...c, image: e.target.result } : c)
          : [...existing, { ...option, image: e.target.result, active: true }];
        return { ...s, homeCategories: nextItem };
      });
    };
    reader.readAsDataURL(file);
  };

  const updateHomeCategory = (key, patch) => {
    setSettings(s => {
      const existing = s.homeCategories || [];
      const option = HOME_CATEGORY_OPTIONS.find(c => c.key === key) || { key, label: key, desc: '' };
      const nextItem = existing.find(c => c.key === key)
        ? existing.map(c => c.key === key ? { ...c, ...patch } : c)
        : [...existing, { ...option, ...patch, active: true }];
      return { ...s, homeCategories: nextItem };
    });
  };

  const updateHomeCategoryAt = (index, patch) => {
    setSettings(s => ({
      ...s,
      homeCategories: (s.homeCategories || []).map((cat, i) => {
        if (i !== index) return cat;
        const next = { ...cat, ...patch };
        if (patch.label !== undefined && (!cat.key || cat.key === slugCategoryKey(cat.label))) {
          next.key = slugCategoryKey(patch.label);
        }
        if (patch.key !== undefined) next.key = slugCategoryKey(patch.key || next.label);
        return next;
      })
    }));
  };

  const addHomeCategory = () => {
    setSettings(s => ({
      ...s,
      homeCategories: [
        ...(s.homeCategories || []),
        { key: `category_${Date.now().toString().slice(-5)}`, label: 'New Category', desc: '', image: '', active: true }
      ]
    }));
  };

  const removeHomeCategory = (index) => {
    setSettings(s => ({
      ...s,
      homeCategories: (s.homeCategories || []).filter((_, i) => i !== index)
    }));
  };

  const statsCards = [
    { label: 'Customers', value: stats.totalUsers || 0, icon: Users, color: '#3B82F6', bg: '#EFF6FF' },
    { label: 'Suppliers', value: stats.totalOwners || 0, icon: Store, color: 'var(--orange)', bg: 'var(--orange-soft)' },
    { label: 'Orders', value: stats.totalOrders || 0, icon: ShoppingBag, color: '#8B5CF6', bg: '#F5F3FF' },
    { label: 'Pending', value: stats.pendingOwners || 0, icon: AlertCircle, color: '#F59E0B', bg: '#FFFBEB' },
    { label: 'Materials', value: stats.totalMaterials || 0, icon: Package, color: 'var(--green)', bg: '#ECFDF5' },
    { label: 'Revenue', value: `₹${(stats.totalRevenue || 0).toLocaleString()}`, icon: TrendingUp, color: '#DC2626', bg: '#FEF2F2' },
    { label: 'Referral Paid', value: `₹${(stats.referralRewards || 0).toLocaleString()}`, icon: Gift, color: '#059669', bg: '#ECFDF5' },
  ];

  const pendingOwners = owners.filter(o => o.verificationStatus === 'pending');

  // Apply search filter
  const searchedOwners = owners.filter(o =>
    !search ||
    o.shopName?.toLowerCase().includes(search.toLowerCase()) ||
    o.user?.email?.toLowerCase().includes(search.toLowerCase()) ||
    o.location?.city?.toLowerCase().includes(search.toLowerCase()) ||
    o.user?.phone?.includes(search)
  );

  // Apply status filter on top of search
  const fOwners = ownerFilter === 'all'
    ? searchedOwners
    : searchedOwners.filter(o => o.verificationStatus === ownerFilter);

  const fCustomers = customers.filter(u =>
    !search ||
    u.name?.toLowerCase().includes(search.toLowerCase()) ||
    u.email?.toLowerCase().includes(search.toLowerCase())
  );

  const fOrders = orders.filter(o =>
    !search ||
    o._id?.includes(search) ||
    o.customer?.name?.toLowerCase().includes(search.toLowerCase()) ||
    o.owner?.shopName?.toLowerCase().includes(search.toLowerCase())
  );

  // Count per status
  const countByStatus = (s) => owners.filter(o => o.verificationStatus === s).length;

  return (
    <div style={{ minHeight: '100vh', background: 'var(--page-bg)' }}>
      <Navbar />
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '18px 16px' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, marginBottom: 18 }}>
          <h1 style={{ fontSize: 20, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Shield size={19} color="var(--orange)" /> Admin Dashboard
          </h1>
          {pendingOwners.length > 0 && (
            <div style={{ background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 10, padding: '6px 14px', fontSize: 13, color: '#92400E', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}
              onClick={() => { setTab('owners'); setOwnerFilter('pending'); }}>
              <AlertCircle size={14} /> {pendingOwners.length} pending — click to review
            </div>
          )}
        </div>

        {/* Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10, marginBottom: 18 }}>
          {statsCards.map((s, i) => (
            <div key={i} style={{ background: 'white', borderRadius: 12, padding: '12px', boxShadow: 'var(--shadow)', cursor: i === 3 ? 'pointer' : 'default' }}
              onClick={() => i === 3 ? (setTab('owners'), setOwnerFilter('pending')) : null}>
              <div style={{ width: 32, height: 32, background: s.bg, borderRadius: 9, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 8 }}>
                <s.icon size={15} color={s.color} />
              </div>
              <div style={{ fontSize: 20, fontWeight: 800, fontFamily: 'var(--font-main)', color: 'var(--dark)', marginBottom: 1 }}>{s.value}</div>
              <div style={{ fontSize: 10, color: 'var(--gray)' }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div style={{ overflowX: 'auto', marginBottom: 14 }}>
          <div style={{ display: 'flex', gap: 4, background: 'white', borderRadius: 12, padding: 5, boxShadow: 'var(--shadow)', width: 'max-content', minWidth: '100%' }}>
            {TABS.map(t => (
              <button key={t} onClick={() => { setTab(t); setSearch(''); setOwnerFilter('all'); }}
                style={{ padding: '8px 18px', borderRadius: 8, border: 'none', background: tab === t ? 'var(--orange)' : 'transparent', color: tab === t ? 'white' : 'var(--gray)', fontWeight: 600, fontSize: 12, cursor: 'pointer', textTransform: 'capitalize', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 5 }}>
                {t === 'user_chats' ? 'User Chats' : t === 'owner_chats' ? 'Shop Owner Chats' : t}
                {t === 'owners' && pendingOwners.length > 0 && (
                  <span style={{ background: tab === t ? 'rgba(255,255,255,0.35)' : 'var(--red)', color: 'white', borderRadius: 10, padding: '1px 6px', fontSize: 10, fontWeight: 700 }}>
                    {pendingOwners.length}
                  </span>
                )}
                {t === 'user_chats' && openUserChatsCount > 0 && (
                  <span style={{ background: tab === t ? 'rgba(255,255,255,0.35)' : 'var(--red)', color: 'white', borderRadius: 10, padding: '1px 6px', fontSize: 10, fontWeight: 700 }}>
                    {openUserChatsCount}
                  </span>
                )}
                {t === 'owner_chats' && openOwnerChatsCount > 0 && (
                  <span style={{ background: tab === t ? 'rgba(255,255,255,0.35)' : 'var(--red)', color: 'white', borderRadius: 10, padding: '1px 6px', fontSize: 10, fontWeight: 700 }}>
                    {openOwnerChatsCount}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Search */}
        {tab !== 'overview' && tab !== 'settings' && tab !== 'user_chats' && tab !== 'owner_chats' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'white', borderRadius: 10, padding: '10px 14px', boxShadow: 'var(--shadow)', marginBottom: 12 }}>
            <Search size={15} color="var(--gray)" />
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder={`Search ${tab}...`}
              style={{ border: 'none', outline: 'none', flex: 1, fontSize: 14 }} />
            {search && <button onClick={() => setSearch('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gray)', display: 'flex' }}><X size={14} /></button>}
          </div>
        )}

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}><div className="spinner" /></div>
        ) : (
          <>
            {/* ── OVERVIEW ── */}
            {tab === 'overview' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {pendingOwners.length > 0 && (
                  <div style={{ background: 'white', borderRadius: 14, padding: 18, boxShadow: 'var(--shadow)' }}>
                    <h3 style={{ fontWeight: 700, fontSize: 15, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
                      <AlertCircle size={15} color="#F59E0B" /> Pending Verifications ({pendingOwners.length})
                    </h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      {pendingOwners.map(owner => (
                        <div key={owner._id} style={{ background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 12, padding: '12px 14px' }}>
                          <div style={{ marginBottom: 8 }}>
                            <div style={{ fontWeight: 700, fontSize: 14 }}>{owner.shopName}</div>
                            <div style={{ color: 'var(--gray)', fontSize: 12 }}>{owner.user?.name} · {owner.user?.phone} · {owner.location?.city}, {owner.location?.state}</div>
                            <div style={{ color: 'var(--gray)', fontSize: 11 }}>{owner.user?.email}</div>
                            {owner.documents?.aadharFileName
                              ? <div style={{ fontSize: 11, color: 'var(--green)', marginTop: 4 }}>📎 {owner.documents.aadharFileName}{owner.documents.workplaceFileName ? ` · ${owner.documents.workplaceFileName}` : ''}</div>
                              : <div style={{ fontSize: 11, color: '#F59E0B', marginTop: 4 }}>⚠️ No documents uploaded</div>
                            }
                          </div>
                          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                            <button onClick={() => setDocOwner(owner)}
                              style={{ display: 'flex', alignItems: 'center', gap: 5, background: '#EFF6FF', border: '1.5px solid #BFDBFE', borderRadius: 8, padding: '7px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer', color: '#1D4ED8' }}>
                              <Eye size={12} /> View Docs
                            </button>
                            <button onClick={() => verifyOwner(owner._id, 'approved')}
                              style={{ display: 'flex', alignItems: 'center', gap: 5, background: 'var(--green)', color: 'white', border: 'none', borderRadius: 8, padding: '7px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                              <CheckCircle size={12} /> Approve
                            </button>
                            <button onClick={() => verifyOwner(owner._id, 'rejected')}
                              style={{ display: 'flex', alignItems: 'center', gap: 5, background: 'var(--red)', color: 'white', border: 'none', borderRadius: 8, padding: '7px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                              <XCircle size={12} /> Reject
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Recent Orders */}
                <div style={{ background: 'white', borderRadius: 14, padding: 18, boxShadow: 'var(--shadow)' }}>
                  <h3 style={{ fontWeight: 700, fontSize: 15, marginBottom: 14 }}>Recent Orders</h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {(stats.recentOrders || []).slice(0, 8).map(order => (
                      <div key={order._id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', background: 'var(--gray-light)', borderRadius: 10, flexWrap: 'wrap', gap: 8 }}>
                        <div>
                          <div style={{ fontWeight: 600, fontSize: 13 }}>{order.customer?.name || 'Customer'}</div>
                          <div style={{ color: 'var(--gray)', fontSize: 11 }}>#{order._id?.slice(-6).toUpperCase()} · {order.owner?.shopName}</div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontWeight: 700, fontSize: 13 }}>₹{order.totalAmount?.toLocaleString()}</div>
                          <span style={{ fontSize: 11, fontWeight: 600, color: STATUS_COLORS[order.status] || 'var(--gray)' }}>
                            {order.status?.replace('_', ' ').toUpperCase()}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* ── OWNERS TAB ── */}
            {tab === 'owners' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

                {/* Filter pills — now clickable buttons with active state */}
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {[
                    { key: 'all', label: 'All', count: owners.length },
                    { key: 'pending', label: 'Pending', count: countByStatus('pending') },
                    { key: 'approved', label: 'Approved', count: countByStatus('approved') },
                    { key: 'rejected', label: 'Rejected', count: countByStatus('rejected') },
                  ].map(f => (
                    <button key={f.key}
                      onClick={() => setOwnerFilter(f.key)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 6,
                        padding: '7px 16px', borderRadius: 20, cursor: 'pointer', fontWeight: 600, fontSize: 12,
                        border: `2px solid ${ownerFilter === f.key ? (f.key === 'approved' ? 'var(--green)' : f.key === 'rejected' ? 'var(--red)' : f.key === 'pending' ? '#F59E0B' : 'var(--orange)') : 'var(--gray-border)'}`,
                        background: ownerFilter === f.key ? (f.key === 'approved' ? '#ECFDF5' : f.key === 'rejected' ? '#FEF2F2' : f.key === 'pending' ? '#FFFBEB' : 'var(--orange-soft)') : 'white',
                        color: ownerFilter === f.key ? (f.key === 'approved' ? 'var(--green)' : f.key === 'rejected' ? 'var(--red)' : f.key === 'pending' ? '#F59E0B' : 'var(--orange)') : 'var(--gray)',
                      }}>
                      {f.label}
                      <span style={{ background: ownerFilter === f.key ? 'rgba(0,0,0,0.12)' : 'var(--gray-light)', borderRadius: 10, padding: '1px 7px', fontSize: 11 }}>
                        {f.count}
                      </span>
                    </button>
                  ))}
                </div>

                {fOwners.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: 40, color: 'var(--gray)', background: 'white', borderRadius: 14 }}>
                    No {ownerFilter !== 'all' ? ownerFilter : ''} suppliers found
                  </div>
                ) : (
                  fOwners.map(owner => (
                    <OwnerCard key={owner._id} owner={owner} onVerify={verifyOwner} onViewDocs={setDocOwner} />
                  ))
                )}
              </div>
            )}

            {/* ── CUSTOMERS TAB ── */}
            {tab === 'customers' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {fCustomers.map(u => (
                  <div key={u._id} style={{ background: 'white', borderRadius: 12, padding: '12px 14px', boxShadow: 'var(--shadow)', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                    <div style={{ width: 36, height: 36, background: 'linear-gradient(135deg,var(--orange),var(--amber))', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 700, fontSize: 14, flexShrink: 0 }}>{u.name?.[0]}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: 14 }}>{u.name}</div>
                      <div style={{ color: 'var(--gray)', fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.email} · {u.phone}</div>
                      <div style={{ color: 'var(--gray)', fontSize: 11 }}>{u.address?.city || '—'} · Joined {new Date(u.createdAt).toLocaleDateString('en-IN')}</div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                      <span style={{ padding: '3px 9px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: u.isActive ? '#ECFDF5' : '#FEF2F2', color: u.isActive ? 'var(--green)' : 'var(--red)' }}>
                        {u.isActive ? 'ACTIVE' : 'BLOCKED'}
                      </span>
                      <button onClick={() => toggleUser(u._id)}
                        style={{ padding: '6px 12px', borderRadius: 7, border: `1.5px solid ${u.isActive ? '#FECACA' : '#A7F3D0'}`, background: u.isActive ? '#FEF2F2' : '#ECFDF5', color: u.isActive ? 'var(--red)' : 'var(--green)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                        {u.isActive ? 'Block' : 'Unblock'}
                      </button>
                    </div>
                  </div>
                ))}
                {fCustomers.length === 0 && <div style={{ textAlign: 'center', padding: 40, color: 'var(--gray)', background: 'white', borderRadius: 14 }}>No customers found</div>}
              </div>
            )}

            {/* ── ORDERS TAB ── */}
            {tab === 'referrals' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {referralPayouts.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: 40, color: 'var(--gray)', background: 'white', borderRadius: 14 }}>
                    No referral payouts found
                  </div>
                ) : referralPayouts.map(row => (
                  <div key={row._id} style={{ background: 'white', borderRadius: 14, padding: 16, boxShadow: 'var(--shadow)' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
                      <div>
                        <div style={{ fontWeight: 800, fontSize: 15 }}>{row.name}</div>
                        <div style={{ color: 'var(--gray)', fontSize: 12 }}>{row.phone} · {row.email}</div>
                        <div style={{ color: row.upiId ? 'var(--green)' : 'var(--red)', fontSize: 12, fontWeight: 700, marginTop: 3 }}>
                          UPI: {row.upiId || 'Not added'}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        <div style={{ background: '#FFFBEB', borderRadius: 10, padding: '8px 11px', minWidth: 95 }}>
                          <div style={{ fontSize: 10, color: '#B45309', fontWeight: 900 }}>PENDING</div>
                          <div style={{ fontWeight: 900 }}>₹{Number(row.pendingAmount || 0).toLocaleString()}</div>
                        </div>
                        <div style={{ background: '#ECFDF5', borderRadius: 10, padding: '8px 11px', minWidth: 95 }}>
                          <div style={{ fontSize: 10, color: 'var(--green)', fontWeight: 900 }}>PAID</div>
                          <div style={{ fontWeight: 900 }}>₹{Number(row.paidAmount || 0).toLocaleString()}</div>
                        </div>
                        <button onClick={() => markReferralPaid(row._id)} className="btn-primary" style={{ padding: '9px 13px', fontSize: 12 }} disabled={!row.pendingAmount || !row.upiId}>
                          Mark Paid
                        </button>
                      </div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {row.credits.map(credit => (
                        <div key={credit._id} style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) auto auto', gap: 10, alignItems: 'center', background: 'var(--gray-light)', borderRadius: 10, padding: '9px 11px' }}>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontWeight: 700, fontSize: 13 }}>{credit.referredName || 'Referral User'}</div>
                            <div style={{ color: 'var(--gray)', fontSize: 11 }}>{credit.referredPhone || 'No phone'} · {credit.referredRole}</div>
                          </div>
                          <span style={{ background: credit.payoutStatus === 'paid' ? '#ECFDF5' : '#FFFBEB', color: credit.payoutStatus === 'paid' ? 'var(--green)' : '#B45309', borderRadius: 20, padding: '3px 8px', fontSize: 10, fontWeight: 900 }}>
                            {(credit.payoutStatus || 'pending').toUpperCase()}
                          </span>
                          <div style={{ fontWeight: 900 }}>₹{Number(credit.amount || 0).toLocaleString()}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {tab === 'orders' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {fOrders.map(order => (
                  <div key={order._id} style={{ background: 'white', borderRadius: 14, padding: 16, boxShadow: 'var(--shadow)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, marginBottom: 12 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <span style={{ fontWeight: 700, fontSize: 13 }}>#{order._id?.slice(-8).toUpperCase()}</span>
                        <span style={{ padding: '2px 9px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: (STATUS_COLORS[order.status] || '#6B7280') + '20', color: STATUS_COLORS[order.status] || 'var(--gray)' }}>
                          {order.status?.replace('_', ' ').toUpperCase()}
                        </span>
                        <span style={{ color: 'var(--gray)', fontSize: 11 }}>{new Date(order.createdAt).toLocaleDateString('en-IN')}</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span style={{ fontWeight: 800, fontSize: 15 }}>₹{order.totalAmount?.toLocaleString()}</span>
                        <select value={order.status} onChange={e => updateOrder(order._id, e.target.value)}
                          style={{ border: '1.5px solid var(--gray-border)', borderRadius: 7, padding: '5px 8px', fontSize: 11, fontWeight: 600, background: 'white', cursor: 'pointer' }}>
                          {['pending','confirmed','processing','out_for_delivery','delivered','cancelled'].map(s => (
                            <option key={s} value={s}>{s.replace('_',' ')}</option>
                          ))}
                        </select>
                        <select value={order.refundStatus || 'none'} onChange={e => updateOrder(order._id, { refundStatus: e.target.value })}
                          style={{ border: '1.5px solid var(--gray-border)', borderRadius: 7, padding: '5px 8px', fontSize: 11, fontWeight: 600, background: 'white', cursor: 'pointer' }}>
                          {['none','requested','approved','rejected','processed'].map(s => (
                            <option key={s} value={s}>Refund {s}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', gap: 8, marginBottom: 10 }}>
                      <div style={{ padding: '8px 11px', background: '#EFF6FF', borderRadius: 8 }}>
                        <div style={{ fontSize: 10, color: '#3B82F6', fontWeight: 700, marginBottom: 2 }}>CUSTOMER</div>
                        <div style={{ fontWeight: 600, fontSize: 13 }}>{order.customer?.name}</div>
                        <div style={{ color: 'var(--gray)', fontSize: 11 }}>{order.customer?.phone}</div>
                      </div>
                      <div style={{ padding: '8px 11px', background: 'var(--orange-soft)', borderRadius: 8 }}>
                        <div style={{ fontSize: 10, color: 'var(--orange)', fontWeight: 700, marginBottom: 2 }}>SUPPLIER</div>
                        <div style={{ fontWeight: 600, fontSize: 13 }}>{order.owner?.shopName}</div>
                        <div style={{ color: 'var(--gray)', fontSize: 11, display: 'flex', alignItems: 'center', gap: 3 }}><MapPin size={9} /> {order.owner?.location?.city}</div>
                      </div>
                      <div style={{ padding: '8px 11px', background: order.paymentStatus?.advance === 'paid' ? '#ECFDF5' : '#FFFBEB', borderRadius: 8 }}>
                        <div style={{ fontSize: 10, color: order.paymentStatus?.advance === 'paid' ? 'var(--green)' : '#F59E0B', fontWeight: 700, marginBottom: 2 }}>PAYMENT</div>
                        <div style={{ fontWeight: 600, fontSize: 12 }}>Adv: {order.paymentStatus?.advance === 'paid' ? `₹${order.advanceAmount} ✓` : 'Pending'}</div>
                        <div style={{ color: 'var(--gray)', fontSize: 11 }}>Bal: ₹{order.remainingAmount?.toLocaleString()} via {order.paymentMethod?.remaining}</div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                      {order.items?.map((item, i) => <span key={i} className="tag" style={{ fontSize: 10 }}>{item.name} × {item.quantity} {item.unit}</span>)}
                    </div>
                    <div style={{ marginTop: 8, color: 'var(--gray)', fontSize: 11, display: 'flex', alignItems: 'center', gap: 4 }}>
                      <MapPin size={10} /> {[order.deliveryAddress?.street, order.deliveryAddress?.city, order.deliveryAddress?.state, order.deliveryAddress?.pincode].filter(Boolean).join(', ')}
                      {order.deliveryAddress?.lat && <span style={{ color: '#3B82F6', marginLeft: 4 }}>📍 GPS</span>}
                    </div>
                  </div>
                ))}
                {fOrders.length === 0 && <div style={{ textAlign: 'center', padding: 40, color: 'var(--gray)', background: 'white', borderRadius: 14 }}>No orders found</div>}
              </div>
            )}

            {/* Settings Tab */}
            {tab === 'settings' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div style={{ background: 'white', borderRadius: 14, padding: 18, boxShadow: 'var(--shadow)' }}>
                  <h3 style={{ fontWeight: 700, fontSize: 16, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <TrendingUp size={16} color="var(--orange)" /> Payment & Revenue Settings
                  </h3>
                  <div style={{ background: 'var(--orange-soft)', borderRadius: 12, padding: 14, marginBottom: 14 }}>
                    <label className="form-label">Advance Amount Collected by Admin</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontWeight: 800, color: 'var(--orange)' }}>₹</span>
                      <input
                        className="form-input"
                        type="number"
                        min="0"
                        value={settings.payment?.advanceAmount ?? 499}
                        onChange={e => setSettings(s => ({ ...s, payment: { ...(s.payment || {}), advanceAmount: e.target.value } }))}
                      />
                    </div>
                    <div style={{ color: 'var(--gray)', fontSize: 11, marginTop: 8, lineHeight: 1.5 }}>
                      This controls the advance payment customers pay online during checkout. The admin revenue card uses paid advance amounts.
                    </div>
                  </div>
                  <button onClick={saveSettings} className="btn-primary" style={{ justifyContent: 'center', padding: '11px 18px' }} disabled={settingsSaving}>
                    <Save size={14} /> {settingsSaving ? 'Saving...' : 'Save Payment Settings'}
                  </button>
                </div>

                <div style={{ background: 'white', borderRadius: 14, padding: 18, boxShadow: 'var(--shadow)' }}>
                  <h3 style={{ fontWeight: 700, fontSize: 16, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Truck size={16} color="var(--orange)" /> Delivery, Tax & Coupons
                  </h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))', gap: 12, marginBottom: 14 }}>
                    <label style={{ background: 'var(--gray-light)', borderRadius: 12, padding: 12 }}>
                      <span className="form-label">Delivery Fee</span>
                      <input className="form-input" type="number" min="0" value={settings.delivery?.baseFee ?? 0}
                        onChange={e => setSettings(s => ({ ...s, delivery: { ...(s.delivery || {}), baseFee: e.target.value } }))} />
                    </label>
                    <label style={{ background: 'var(--gray-light)', borderRadius: 12, padding: 12 }}>
                      <span className="form-label">Free Delivery Above</span>
                      <input className="form-input" type="number" min="0" value={settings.delivery?.freeAbove ?? 0}
                        onChange={e => setSettings(s => ({ ...s, delivery: { ...(s.delivery || {}), freeAbove: e.target.value } }))} />
                    </label>
                    <label style={{ background: 'var(--gray-light)', borderRadius: 12, padding: 12 }}>
                      <span className="form-label">Tax Percent</span>
                      <input className="form-input" type="number" min="0" value={settings.tax?.percent ?? 0}
                        onChange={e => setSettings(s => ({ ...s, tax: { ...(s.tax || {}), percent: e.target.value } }))} />
                    </label>
                  </div>
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, fontSize: 13 }}>
                      <input type="checkbox" checked={settings.delivery?.enabled !== false}
                        onChange={e => setSettings(s => ({ ...s, delivery: { ...(s.delivery || {}), enabled: e.target.checked } }))} />
                      Enable delivery fee
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, fontSize: 13 }}>
                      <input type="checkbox" checked={settings.tax?.enabled === true}
                        onChange={e => setSettings(s => ({ ...s, tax: { ...(s.tax || {}), enabled: e.target.checked } }))} />
                      Enable tax
                    </label>
                  </div>
                  <div style={{ background: 'var(--orange-soft)', borderRadius: 12, padding: 14, marginBottom: 14 }}>
                    <div style={{ fontWeight: 800, fontSize: 13, marginBottom: 10 }}>Coupons</div>
                    {(settings.coupons || []).map((coupon, index) => (
                      <div key={index} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 110px 110px 90px auto', gap: 8, marginBottom: 8 }}>
                        <input className="form-input" placeholder="CODE" value={coupon.code || ''} onChange={e => setSettings(s => ({ ...s, coupons: (s.coupons || []).map((c, i) => i === index ? { ...c, code: e.target.value.toUpperCase() } : c) }))} />
                        <input className="form-input" placeholder="Title" value={coupon.title || ''} onChange={e => setSettings(s => ({ ...s, coupons: (s.coupons || []).map((c, i) => i === index ? { ...c, title: e.target.value } : c) }))} />
                        <select className="form-input" value={coupon.type || 'flat'} onChange={e => setSettings(s => ({ ...s, coupons: (s.coupons || []).map((c, i) => i === index ? { ...c, type: e.target.value } : c) }))}>
                          <option value="flat">Flat</option>
                          <option value="percent">Percent</option>
                        </select>
                        <input className="form-input" type="number" placeholder="Value" value={coupon.value || ''} onChange={e => setSettings(s => ({ ...s, coupons: (s.coupons || []).map((c, i) => i === index ? { ...c, value: e.target.value } : c) }))} />
                        <input className="form-input" type="number" placeholder="Min" value={coupon.minOrder || ''} onChange={e => setSettings(s => ({ ...s, coupons: (s.coupons || []).map((c, i) => i === index ? { ...c, minOrder: e.target.value } : c) }))} />
                        <button onClick={() => setSettings(s => ({ ...s, coupons: (s.coupons || []).filter((_, i) => i !== index) }))} style={{ border: 'none', borderRadius: 8, background: '#FEF2F2', color: 'var(--red)', fontWeight: 800 }}>Remove</button>
                      </div>
                    ))}
                    <button onClick={() => setSettings(s => ({ ...s, coupons: [...(s.coupons || []), { code: 'SAVE50', title: 'Launch coupon', type: 'flat', value: 50, minOrder: 500, active: true }] }))} className="btn-outline" style={{ padding: '8px 12px', fontSize: 12 }}>
                      Add Coupon
                    </button>
                  </div>
                  <div style={{ background: '#EFF6FF', borderRadius: 12, padding: 14, marginBottom: 14 }}>
                    <div style={{ fontWeight: 800, fontSize: 13, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}><Image size={14} /> Homepage Banners</div>
                    {(settings.banners || []).map((banner, index) => (
                      <div key={index} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: 8, marginBottom: 8 }}>
                        <input className="form-input" placeholder="Title" value={banner.title || ''} onChange={e => setSettings(s => ({ ...s, banners: (s.banners || []).map((b, i) => i === index ? { ...b, title: e.target.value } : b) }))} />
                        <input className="form-input" placeholder="Subtitle" value={banner.subtitle || ''} onChange={e => setSettings(s => ({ ...s, banners: (s.banners || []).map((b, i) => i === index ? { ...b, subtitle: e.target.value } : b) }))} />
                        <input className="form-input" placeholder="/shops" value={banner.link || ''} onChange={e => setSettings(s => ({ ...s, banners: (s.banners || []).map((b, i) => i === index ? { ...b, link: e.target.value } : b) }))} />
                        <button onClick={() => setSettings(s => ({ ...s, banners: (s.banners || []).filter((_, i) => i !== index) }))} style={{ border: 'none', borderRadius: 8, background: '#FEF2F2', color: 'var(--red)', fontWeight: 800 }}>Remove</button>
                      </div>
                    ))}
                    <button onClick={() => setSettings(s => ({ ...s, banners: [...(s.banners || []), { title: 'Best construction deals', subtitle: 'Fresh prices from verified nearby shops', link: '/shops', active: true }] }))} className="btn-outline" style={{ padding: '8px 12px', fontSize: 12 }}>
                      Add Banner
                    </button>
                  </div>
                  <div style={{ background: '#ECFDF5', borderRadius: 12, padding: 14, marginBottom: 14 }}>
                    <div style={{ fontWeight: 800, fontSize: 13, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}><Package size={14} /> Homepage Brands</div>
                    {(settings.homeBrands || []).map((brand, index) => (
                      <div key={index} style={{ background: 'white', borderRadius: 12, padding: 12, marginBottom: 10, border: '1px solid var(--gray-border)' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '64px 1fr 1fr 1fr auto', gap: 8, alignItems: 'center' }}>
                          <div style={{ width: 52, height: 52, borderRadius: 12, border: '1.5px solid var(--gray-border)', background: 'var(--gray-light)', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {brand.image ? <img src={brand.image} alt={brand.name || 'Brand'} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <Image size={18} color="var(--gray)" />}
                          </div>
                          <input className="form-input" placeholder="Brand name" value={brand.name || ''} onChange={e => setSettings(s => ({ ...s, homeBrands: (s.homeBrands || []).map((b, i) => i === index ? { ...b, name: e.target.value } : b) }))} />
                          <input className="form-input" placeholder="Short related text" value={brand.text || ''} onChange={e => setSettings(s => ({ ...s, homeBrands: (s.homeBrands || []).map((b, i) => i === index ? { ...b, text: e.target.value } : b) }))} />
                          <input className="form-input" placeholder="/shops?search=ACC" value={brand.link || ''} onChange={e => setSettings(s => ({ ...s, homeBrands: (s.homeBrands || []).map((b, i) => i === index ? { ...b, link: e.target.value } : b) }))} />
                          <button onClick={() => setSettings(s => ({ ...s, homeBrands: (s.homeBrands || []).filter((_, i) => i !== index) }))} style={{ border: 'none', borderRadius: 8, background: '#FEF2F2', color: 'var(--red)', fontWeight: 800, padding: '9px 10px' }}>Remove</button>
                        </div>
                        <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                          <input type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" onChange={e => uploadHomeBrandImage(index, e.target.files?.[0])} style={{ fontSize: 12 }} />
                          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700 }}>
                            <input type="checkbox" checked={brand.active !== false} onChange={e => setSettings(s => ({ ...s, homeBrands: (s.homeBrands || []).map((b, i) => i === index ? { ...b, active: e.target.checked } : b) }))} />
                            Show on home
                          </label>
                        </div>
                      </div>
                    ))}
                    <button onClick={() => setSettings(s => ({ ...s, homeBrands: [...(s.homeBrands || []), { name: 'ACC Cement', text: 'Trusted cement for strong construction work', image: '', link: '/shops?search=ACC', active: true }] }))} className="btn-outline" style={{ padding: '8px 12px', fontSize: 12 }}>
                      Add Brand
                    </button>
                  </div>
                  <div style={{ background: 'var(--orange-softer)', borderRadius: 12, padding: 14, marginBottom: 14 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
                      <div>
                        <div style={{ fontWeight: 800, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}><Image size={14} /> Categories</div>
                        <div style={{ color: 'var(--gray)', fontSize: 11, marginTop: 2 }}>These categories show in shop and material filters.</div>
                      </div>
                      <button onClick={addHomeCategory} className="btn-outline" style={{ padding: '8px 12px', fontSize: 12 }}>Add Category</button>
                    </div>
                    <div style={{ display: 'grid', gap: 10 }}>
                      {(settings.homeCategories || []).map((category, index) => (
                        <div key={`${category.key || 'category'}-${index}`} style={{ background: 'white', borderRadius: 12, padding: 12, border: '1px solid var(--gray-border)' }}>
                          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                            <div style={{ width: 52, height: 52, borderRadius: 12, background: 'var(--gray-light)', border: '1.5px solid var(--gray-border)', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              {category.image ? <img src={category.image} alt={category.label || 'Category'} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <Package size={20} color="var(--gray)" />}
                            </div>
                            <input className="form-input" placeholder="Category name" value={category.label || ''} onChange={e => updateHomeCategoryAt(index, { label: e.target.value })} style={{ flex: '1 1 150px' }} />
                            <input className="form-input" placeholder="category_key" value={category.key || ''} onChange={e => updateHomeCategoryAt(index, { key: e.target.value })} style={{ flex: '1 1 120px' }} />
                            <input className="form-input" placeholder="Short text" value={category.desc || ''} onChange={e => updateHomeCategoryAt(index, { desc: e.target.value })} style={{ flex: '1 1 170px' }} />
                            <button onClick={() => removeHomeCategory(index)} style={{ border: 'none', borderRadius: 8, background: '#FEF2F2', color: 'var(--red)', fontWeight: 800, padding: '9px 10px' }}>Remove</button>
                          </div>
                          <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                            <input type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" onChange={e => uploadHomeCategoryImage(category.key, e.target.files?.[0])} style={{ fontSize: 12 }} />
                            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700 }}>
                              <input type="checkbox" checked={category.active !== false} onChange={e => updateHomeCategoryAt(index, { active: e.target.checked })} />
                              Show in filters
                            </label>
                            {category.image && (
                              <button onClick={() => updateHomeCategoryAt(index, { image: '' })} style={{ border: 'none', borderRadius: 8, background: '#FEF2F2', color: 'var(--red)', fontWeight: 800, padding: '7px 9px', fontSize: 12 }}>Remove image</button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <button onClick={saveSettings} className="btn-primary" style={{ justifyContent: 'center', padding: '11px 18px' }} disabled={settingsSaving}>
                    <Save size={14} /> {settingsSaving ? 'Saving...' : 'Save Delivery & Coupon Settings'}
                  </button>
                </div>

                <div style={{ background: 'white', borderRadius: 14, padding: 18, boxShadow: 'var(--shadow)' }}>
                  <h3 style={{ fontWeight: 700, fontSize: 16, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Settings size={16} color="var(--orange)" /> Referral Settings
                  </h3>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))', gap: 12, marginBottom: 14 }}>
                    <div style={{ background: 'var(--orange-soft)', borderRadius: 12, padding: 14 }}>
                      <label className="form-label">Customer Signup Reward</label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontWeight: 800, color: 'var(--orange)' }}>₹</span>
                        <input
                          className="form-input"
                          type="number"
                          min="0"
                          value={settings.referral?.customerReward ?? 20}
                          onChange={e => setSettings(s => ({ ...s, referral: { ...s.referral, customerReward: e.target.value } }))}
                        />
                      </div>
                    </div>

                    <div style={{ background: '#ECFDF5', borderRadius: 12, padding: 14 }}>
                      <label className="form-label">Owner Signup Reward</label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontWeight: 800, color: 'var(--green)' }}>₹</span>
                        <input
                          className="form-input"
                          type="number"
                          min="0"
                          value={settings.referral?.ownerReward ?? 50}
                          onChange={e => setSettings(s => ({ ...s, referral: { ...s.referral, ownerReward: e.target.value } }))}
                        />
                      </div>
                    </div>
                  </div>

                  <label style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'var(--gray-light)', borderRadius: 10, padding: '11px 12px', marginBottom: 14, cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={settings.referral?.enabled !== false}
                      onChange={e => setSettings(s => ({ ...s, referral: { ...s.referral, enabled: e.target.checked } }))}
                    />
                    <span style={{ fontWeight: 700, fontSize: 13 }}>Enable referral rewards</span>
                  </label>

                  <button onClick={saveSettings} className="btn-primary" style={{ justifyContent: 'center', padding: '11px 18px' }} disabled={settingsSaving}>
                    <Save size={14} /> {settingsSaving ? 'Saving...' : 'Save Settings'}
                  </button>
                </div>

                <div style={{ background: 'white', borderRadius: 14, padding: 18, boxShadow: 'var(--shadow)' }}>
                  <h3 style={{ fontWeight: 700, fontSize: 16, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Shield size={16} color="var(--orange)" /> Logo & Favicon
                  </h3>

                  <div style={{ marginBottom: 14 }}>
                    <label className="form-label">App Name</label>
                    <input
                      className="form-input"
                      value={settings.branding?.appName || 'Material Stock'}
                      onChange={e => setSettings(s => ({ ...s, branding: { ...(s.branding || {}), appName: e.target.value } }))}
                      placeholder="Material Stock"
                    />
                  </div>

                  <div style={{ marginBottom: 14 }}>
                    <label className="form-label">Support / Contact Email</label>
                    <input
                      type="email"
                      className="form-input"
                      value={settings.branding?.supportEmail || 'support@materialstock.in'}
                      onChange={e => setSettings(s => ({ ...s, branding: { ...(s.branding || {}), supportEmail: e.target.value } }))}
                      placeholder="support@materialstock.in"
                    />
                    <div style={{ fontSize: 12, color: 'var(--gray)', marginTop: 4 }}>
                      This email will be displayed on the Footer, Contact Page, Invoices, and Support sections across the platform.
                    </div>
                  </div>

                  <div style={{ marginBottom: 16 }}>
                    <div style={{ fontWeight: 800, fontSize: 13, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 7 }}>
                      <Palette size={15} color="var(--orange)" /> Website Theme
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(168px,1fr))', gap: 10 }}>
                      {THEME_OPTIONS.map(theme => {
                        const active = (settings.branding?.theme || 'forest') === theme.key;
                        return (
                          <button
                            key={theme.key}
                            type="button"
                            onClick={() => selectTheme(theme.key)}
                            disabled={settingsSaving}
                            style={{
                              textAlign: 'left',
                              border: `1.8px solid ${active ? 'var(--orange)' : 'var(--gray-border)'}`,
                              background: active ? 'var(--orange-softer)' : 'white',
                              borderRadius: 12,
                              padding: 12,
                              cursor: settingsSaving ? 'not-allowed' : 'pointer'
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 8 }}>
                              {theme.colors.map(color => (
                                <span key={color} style={{ width: 22, height: 22, borderRadius: '50%', background: color, border: '2px solid white', boxShadow: '0 0 0 1px rgba(0,0,0,0.08)' }} />
                              ))}
                              {active && <span className="badge badge-orange" style={{ marginLeft: 'auto', fontSize: 10 }}>Active</span>}
                            </div>
                            <div style={{ fontWeight: 900, fontSize: 13 }}>{theme.name}</div>
                            <div style={{ color: 'var(--gray)', fontSize: 11, marginTop: 2 }}>{theme.note}</div>
                          </button>
                        );
                      })}
                    </div>
                    <div style={{ color: 'var(--gray)', fontSize: 11, marginTop: 8 }}>
                      Click any theme to apply the complete website colour set immediately.
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: 14 }}>
                    {[
                      { field: 'logoImage', label: 'Navbar & Footer Logo', hint: 'Shown in navbar and footer. Wide PNG/JPG/WebP logo works best.', defaultPreview: '/material-stock-logo.jpg' },
                      { field: 'darkLogoImage', label: 'Dark Mode Logo', hint: 'Used on dark footer/sections. Upload a light or white version for best contrast.', defaultPreview: settings.branding?.logoImage || '/material-stock-logo.jpg', darkPreview: true },
                      { field: 'faviconImage', label: 'Bright Mode Favicon', hint: 'Shown in browser tab for light/bright tabs. Use square PNG/ICO/WebP.' },
                      { field: 'darkFaviconImage', label: 'Dark Mode Favicon', hint: 'Shown in browser tab for dark-mode tabs. Use a light icon for contrast.', darkPreview: true }
                    ].map(item => (
                      <div key={item.field} style={{ background: 'var(--gray-light)', borderRadius: 12, padding: 14 }}>
                        <label className="form-label">{item.label}</label>
                        <div style={{ fontSize: 11, color: 'var(--gray)', marginBottom: 10 }}>{item.hint}</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                          <div style={{ width: item.field.includes('Favicon') || item.field === 'faviconImage' ? 58 : 132, height: 58, background: item.darkPreview ? 'var(--dark)' : 'white', border: '1.5px solid var(--gray-border)', borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0, padding: item.field.includes('Favicon') || item.field === 'faviconImage' ? 0 : 8 }}>
                            {settings.branding?.[item.field] || item.defaultPreview ? (
                              <img src={settings.branding?.[item.field] || item.defaultPreview} alt={item.label} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                            ) : (
                              <Package size={22} color="var(--gray)" />
                            )}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <input
                              type="file"
                              accept="image/png,image/jpeg,image/webp,image/x-icon,image/svg+xml"
                              onChange={e => uploadBrandImage(item.field, e.target.files?.[0])}
                              style={{ width: '100%', fontSize: 12 }}
                            />
                            <div style={{ fontSize: 10, color: 'var(--gray)', marginTop: 4 }}>Max 2MB</div>
                          </div>
                        </div>
                        {settings.branding?.[item.field] && (
                          <button
                            type="button"
                            onClick={() => setSettings(s => ({ ...s, branding: { ...(s.branding || {}), [item.field]: '' } }))}
                            style={{ background: '#FEF2F2', color: 'var(--red)', border: '1.5px solid #FECACA', borderRadius: 8, padding: '7px 10px', fontSize: 12, fontWeight: 700 }}
                          >
                            Remove {item.label}
                          </button>
                        )}
                      </div>
                    ))}
                  </div>

                  <button onClick={saveSettings} className="btn-primary" style={{ justifyContent: 'center', padding: '11px 18px', marginTop: 14 }} disabled={settingsSaving}>
                    <Save size={14} /> {settingsSaving ? 'Saving...' : 'Save Branding'}
                  </button>
                </div>

                <div style={{ background: 'white', borderRadius: 14, padding: 18, boxShadow: 'var(--shadow)' }}>
                  <h3 style={{ fontWeight: 700, fontSize: 16, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Gift size={16} color="var(--green)" /> Current Program
                  </h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: 10 }}>
                    <div style={{ background: 'var(--gray-light)', borderRadius: 10, padding: '10px 12px' }}>
                      <div style={{ fontSize: 10, color: 'var(--gray)', fontWeight: 800 }}>CUSTOMER</div>
                      <div style={{ fontSize: 20, fontWeight: 900 }}>₹{settings.referral?.customerReward ?? 20}</div>
                    </div>
                    <div style={{ background: 'var(--gray-light)', borderRadius: 10, padding: '10px 12px' }}>
                      <div style={{ fontSize: 10, color: 'var(--gray)', fontWeight: 800 }}>OWNER</div>
                      <div style={{ fontSize: 20, fontWeight: 900 }}>₹{settings.referral?.ownerReward ?? 50}</div>
                    </div>
                    <div style={{ background: settings.referral?.enabled !== false ? '#ECFDF5' : '#FEF2F2', borderRadius: 10, padding: '10px 12px' }}>
                      <div style={{ fontSize: 10, color: 'var(--gray)', fontWeight: 800 }}>STATUS</div>
                      <div style={{ fontSize: 20, fontWeight: 900, color: settings.referral?.enabled !== false ? 'var(--green)' : 'var(--red)' }}>
                        {settings.referral?.enabled !== false ? 'Active' : 'Off'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {tab === 'user_chats' && <AdminSupportChats roleType="customer" selectedTicketId={urlTicket} onChatRead={fetchChatCounts} />}
            {tab === 'owner_chats' && <AdminSupportChats roleType="owner" selectedTicketId={urlTicket} onChatRead={fetchChatCounts} />}
          </>
        )}
      </div>

      {/* Document Viewer Modal */}
      {docOwner && <DocModal owner={docOwner} onClose={() => setDocOwner(null)} />}
    </div>
  );
}

function AdminSupportChats({ roleType, selectedTicketId, onChatRead }) {
  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeChat, setActiveChat] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [replyText, setReplyText] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const messagesContainerRef = useRef(null);

  useEffect(() => {
    fetchAdminChats();
  }, [roleType, selectedTicketId]);

  useEffect(() => {
    const interval = setInterval(() => {
      fetchAdminChats(true);
    }, 5000);
    return () => clearInterval(interval);
  }, [roleType, activeChat?._id]);

  useEffect(() => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  }, [activeChat?.messages]);

  useEffect(() => {
    if (activeChat && activeChat.adminRead === false) {
      API.put(`/support/chats/${activeChat._id}/read`).then(res => {
        setActiveChat(res.data);
        setChats(prev => prev.map(c => c._id === res.data._id ? res.data : c));
        if (onChatRead) onChatRead();
      }).catch(() => {});
    }
  }, [activeChat?._id]);

  const fetchAdminChats = async (isBackground = false) => {
    if (!isBackground) setLoading(true);
    try {
      const res = await API.get(`/support/admin/chats?userRole=${roleType}`);
      setChats(res.data);
      if (selectedTicketId) {
        const target = res.data.find(c => c._id === selectedTicketId);
        if (target) {
          setActiveChat(target);
          return;
        }
      }
      if (activeChat) {
        const updated = res.data.find(c => c._id === activeChat._id);
        if (updated) setActiveChat(updated);
      } else if (res.data.length > 0) {
        setActiveChat(res.data[0]);
      }
    } catch (err) {
      if (!isBackground) toast.error('Failed to load support chats');
    } finally {
      if (!isBackground) setLoading(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!replyText.trim() || !activeChat) return;
    setSendingReply(true);
    try {
      const res = await API.post(`/support/chats/${activeChat._id}/messages`, {
        message: replyText.trim()
      });
      setReplyText('');
      setActiveChat(res.data);
      setChats(prev => prev.map(c => c._id === res.data._id ? res.data : c));
      toast.success('Reply sent');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send reply');
    } finally {
      setSendingReply(false);
    }
  };

  const handleResolveTicket = async () => {
    if (!activeChat) return;
    try {
      const res = await API.put(`/support/chats/${activeChat._id}/resolve`);
      toast.success('Ticket marked as resolved');
      setActiveChat(res.data);
      setChats(prev => prev.map(c => c._id === res.data._id ? res.data : c));
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to resolve ticket');
    }
  };

  const filteredChats = chats.filter(c => {
    const matchesStatus = statusFilter === 'all' || c.status === statusFilter;
    const q = searchQuery.toLowerCase();
    const matchesSearch = !q ||
      c.user?.name?.toLowerCase().includes(q) ||
      c.user?.email?.toLowerCase().includes(q) ||
      c.issue?.toLowerCase().includes(q) ||
      c.orderId?.toLowerCase().includes(q);
    return matchesStatus && matchesSearch;
  });

  return (
    <div style={{ background: 'white', borderRadius: 14, padding: 18, boxShadow: 'var(--shadow)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
        <h2 style={{ fontSize: 18, fontWeight: 800, display: 'flex', alignItems: 'center', gap: 8 }}>
          <MessageSquare size={18} color="var(--orange)" />
          {roleType === 'customer' ? 'Customer Support Chats' : 'Shop Owner Support Chats'}
        </h2>
        <div style={{ display: 'flex', gap: 6 }}>
          {['all', 'open', 'resolved'].map(st => (
            <button
              key={st}
              onClick={() => setStatusFilter(st)}
              style={{
                padding: '6px 14px',
                borderRadius: 20,
                fontSize: 12,
                fontWeight: 700,
                border: 'none',
                cursor: 'pointer',
                background: statusFilter === st ? 'var(--orange)' : 'var(--gray-light)',
                color: statusFilter === st ? 'white' : 'var(--gray)',
                textTransform: 'capitalize'
              }}
            >
              {st} ({chats.filter(c => st === 'all' || c.status === st).length})
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}><div className="spinner" /></div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', border: '1px solid var(--gray-border)', borderRadius: 12, overflow: 'hidden', minHeight: 480 }}>
          {/* Left Column: Tickets list */}
          <div style={{ borderRight: '1px solid var(--gray-border)', padding: 14, background: 'var(--gray-light)', display: 'flex', flexDirection: 'column' }}>
            <div style={{ marginBottom: 10 }}>
              <input
                className="form-input"
                style={{ fontSize: 12, padding: '7px 10px' }}
                placeholder="Search user, issue, order ID..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
              />
            </div>

            {filteredChats.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 40, color: 'var(--gray)', fontSize: 13 }}>
                No support chats found.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flex: 1, overflowY: 'auto', maxHeight: 420 }}>
                {filteredChats.map(chat => {
                  const isSelected = activeChat?._id === chat._id;
                  return (
                    <div
                      key={chat._id}
                      onClick={() => setActiveChat(chat)}
                      style={{
                        padding: 12,
                        borderRadius: 10,
                        background: isSelected ? 'white' : 'transparent',
                        border: `1.5px solid ${isSelected ? 'var(--orange)' : 'transparent'}`,
                        cursor: 'pointer',
                        boxShadow: isSelected ? 'var(--shadow)' : 'none',
                        transition: 'all 0.15s ease'
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
                        <span style={{ fontWeight: 800, fontSize: 13, color: 'var(--dark)', display: 'flex', alignItems: 'center' }}>
                          {chat.user?.name || 'User'}
                          {!chat.adminRead && chat.status === 'open' && (
                            <span style={{ fontSize: 9, fontWeight: 800, background: 'var(--orange)', color: 'white', borderRadius: 8, padding: '1px 5px', marginLeft: 6 }}>
                              NEW
                            </span>
                          )}
                        </span>
                        <span style={{
                          fontSize: 10,
                          fontWeight: 800,
                          padding: '2px 7px',
                          borderRadius: 12,
                          background: chat.status === 'resolved' ? '#ECFDF5' : '#FFFBEB',
                          color: chat.status === 'resolved' ? 'var(--green)' : '#D97706'
                        }}>
                          {chat.status.toUpperCase()}
                        </span>
                      </div>
                      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--orange)' }}>{chat.issue}</div>
                      {chat.orderId && (
                        <div style={{ fontSize: 11, color: 'var(--gray)', marginTop: 2 }}>
                          Order ID: #{chat.orderId}
                        </div>
                      )}
                      <div style={{ fontSize: 11, color: 'var(--gray)', marginTop: 4, display: 'flex', justifyContent: 'space-between' }}>
                        <span>{chat.user?.email}</span>
                        <span>{new Date(chat.updatedAt).toLocaleDateString()}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Right Column: Active Chat & Details */}
          <div style={{ padding: 16, display: 'flex', flexDirection: 'column', background: 'white' }}>
            {activeChat ? (
              <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                {/* Header info */}
                <div style={{ paddingBottom: 12, borderBottom: '1px solid var(--gray-border)', marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 10 }}>
                  <div>
                    <div style={{ fontWeight: 900, fontSize: 16, color: 'var(--dark)' }}>{activeChat.user?.name || 'User'}</div>
                    <div style={{ fontSize: 12, color: 'var(--gray)', display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                      <span>✉️ {activeChat.user?.email}</span>
                      <span>📞 {activeChat.user?.phone}</span>
                    </div>
                    <div style={{ marginTop: 6, fontSize: 13, fontWeight: 800, color: 'var(--orange)' }}>
                      Issue: {activeChat.issue} {activeChat.orderId ? `(Order ID: ${activeChat.orderId})` : ''}
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{
                      fontSize: 11,
                      fontWeight: 800,
                      padding: '4px 10px',
                      borderRadius: 12,
                      background: activeChat.status === 'resolved' ? '#ECFDF5' : '#FFFBEB',
                      color: activeChat.status === 'resolved' ? 'var(--green)' : '#D97706'
                    }}>
                      {activeChat.status.toUpperCase()}
                    </span>
                    {activeChat.status !== 'resolved' && (
                      <button
                        onClick={handleResolveTicket}
                        style={{ background: '#ECFDF5', color: 'var(--green)', border: '1px solid #A7F3D0', borderRadius: 8, padding: '6px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}
                      >
                        <CheckCircle2 size={14} /> Resolve Ticket
                      </button>
                    )}
                  </div>
                </div>

                {/* Messages list */}
                <div ref={messagesContainerRef} style={{ flex: 1, overflowY: 'auto', maxHeight: 300, paddingRight: 6, display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 14 }}>
                  {activeChat.messages?.map((msg, idx) => {
                    const isAdminMsg = msg.senderRole === 'admin';
                    return (
                      <div
                        key={idx}
                        style={{
                          alignSelf: isAdminMsg ? 'flex-end' : 'flex-start',
                          maxWidth: '80%',
                          background: isAdminMsg ? 'var(--orange)' : '#F3F4F6',
                          color: isAdminMsg ? 'white' : 'var(--dark)',
                          padding: '10px 14px',
                          borderRadius: 14,
                          borderBottomRightRadius: isAdminMsg ? 2 : 14,
                          borderBottomLeftRadius: isAdminMsg ? 14 : 2,
                          boxShadow: '0 1px 3px rgba(0,0,0,0.06)'
                        }}
                      >
                        <div style={{ fontSize: 10, fontWeight: 800, color: isAdminMsg ? 'rgba(255,255,255,0.85)' : 'var(--gray)', marginBottom: 2 }}>
                          {msg.senderName} ({msg.senderRole.toUpperCase()})
                        </div>
                        <div style={{ fontSize: 13, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
                          {msg.message}
                        </div>
                        <div style={{ fontSize: 9, textAlign: 'right', marginTop: 4, color: isAdminMsg ? 'rgba(255,255,255,0.75)' : 'var(--gray)' }}>
                          {new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Input box */}
                {activeChat.status === 'resolved' ? (
                  <div style={{ background: '#ECFDF5', color: 'var(--green)', padding: '10px 14px', borderRadius: 10, fontSize: 12, fontWeight: 700, textAlign: 'center' }}>
                    This support ticket is resolved.
                  </div>
                ) : (
                  <form onSubmit={handleSendMessage} style={{ display: 'flex', gap: 8 }}>
                    <input
                      className="form-input"
                      style={{ flex: 1 }}
                      placeholder="Type reply as Support Admin..."
                      value={replyText}
                      onChange={e => setReplyText(e.target.value)}
                    />
                    <button type="submit" className="btn-primary" style={{ padding: '0 18px', display: 'flex', alignItems: 'center', gap: 6 }} disabled={sendingReply}>
                      <Send size={15} /> Reply
                    </button>
                  </form>
                )}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: 60, color: 'var(--gray)' }}>
                Select a support ticket from the list to view details and reply.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

